/**
 * Registers the service worker generated by vite-plugin-pwa (see vite.config.ts for what it does and, more
 * importantly, what it refuses to do).
 *
 * Registered by hand rather than through `virtual:pwa-register` so that the scope is written down where anyone
 * reading this file can see it, and so the portal carries no runtime dependency for four lines of platform API.
 * `registerType: 'autoUpdate'` builds skipWaiting + clientsClaim into the worker itself, so a new deploy takes
 * over as soon as it has installed — no "a new version is available" ribbon. For a watchdog that is the right
 * trade: nobody should be reading last week's shell because they never clicked a button, and no page here holds
 * unsaved work.
 *
 * The scope is the whole origin because the portal *is* the origin — but the worker deliberately keeps its hands
 * off /admin, /api, /sso and /impersonate (vite.config.ts). The push and notificationclick handlers are pulled
 * into the worker from /push-sw.js, and `pushManager.subscribe` runs against exactly this registration.
 *
 * `updateViaCache: 'none'`: the browser already bypasses the HTTP cache for the worker script itself, but by
 * default it does NOT for anything the worker importScripts. Without this, a customer could keep an old
 * /push-sw.js — the file that decides what a notification looks like — long after the deploy that replaced it.
 */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  // No worker is emitted by `vite dev`, so registering there would only log a 404.
  if (!import.meta.env.PROD) return;

  navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).catch((error: unknown) => {
    // An unavailable worker must never take the portal down with it.
    console.warn('[pwa] service worker registration failed', error);
  });
}
